import React, { useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { ChallengeForm } from "../components/challenges/ChallengeForm"
import { DropdownMenu } from "../components/inputs/DropdownMenu"
import { Modal } from "../components/modals/Modal"
import { TopBar } from "../components/navigation/TopBar"
import { Table } from "../components/tables/Table"
import { CHALLENGE_COLUMNS, COLUMN_DESCRIPTION_KEYS, STATUS_FILTER_DESCRIPTION_KEYS, STATUS_FILTER_OPTIONS } from "../constants/challenges"
import { TableAction } from "../constants/type"
import { challengeService } from "../services/challenge/challenge.service"
import {
  ChallengeSortField,
  ChallengeStatusFilter,
  CreateChallengeInput,
  GameChallenge,
  UpdateChallengeInput
} from "../services/challenge/type"

const isFinished = (row: GameChallenge): boolean => {
  if (row.winner_id) return true
  if (row.end_at && new Date(row.end_at) < new Date()) return true
  return false
}

export function Challenges() {
  const { t } = useTranslation("translation", { useSuspense: false })

  const [editRow, setEditRow] = useState<GameChallenge | null>(null)
  const [openModal, setOpenModal] = useState(false)
  const [isCreate, setIsCreate] = useState(false)
  const [isDuplicate, setIsDuplicate] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [tableKey, setTableKey] = useState(0)
  const [statusFilter, setStatusFilter] = useState<ChallengeStatusFilter>("all")

  const localizedColumns = useMemo(() => {
    return CHALLENGE_COLUMNS.map((col) => ({
      ...col,
      title: t(col.title)
    }))
  }, [t])

  const localizedStatusOptions = useMemo(() => {
    return STATUS_FILTER_OPTIONS.map((opt) => ({
      name: t(opt.labelKey),
      value: opt.value
    }))
  }, [t])

  const refreshTable = useCallback(() => setTableKey((k) => k + 1), [])

  const tableHandler = useCallback(
    async (page: number, pageSize: number, _id?: unknown, _filters?: unknown, orderBy?: string, order?: string) => {
      const result = await challengeService.getAll({
        status_filter: statusFilter,
        limit: pageSize,
        offset: (page - 1) * pageSize,
        sort_by: orderBy !== "all" ? (orderBy as ChallengeSortField) : undefined,
        sort_order: order as "asc" | "desc"
      })

      return {
        data: result.data,
        page,
        total: result.total,
        total_pages: Math.ceil(result.total / pageSize)
      }
    },
    [statusFilter]
  )

  useEffect(() => {
    refreshTable()
  }, [statusFilter, refreshTable])

  const closeModal = useCallback(() => {
    setOpenModal(false)
    setEditRow(null)
    setIsDuplicate(false)
  }, [])

  const openCreate = useCallback(() => {
    setEditRow(null)
    setIsCreate(true)
    setIsDuplicate(false)
    setOpenModal(true)
  }, [])

  const openEdit = useCallback((row: GameChallenge) => {
    setEditRow(row)
    setIsCreate(false)
    setIsDuplicate(false)
    setOpenModal(true)
  }, [])

  const openDuplicate = useCallback((row: GameChallenge) => {
    setEditRow({
      ...row,
      id: undefined as unknown as string,
      winner_id: null,
      won_at: null,
      end_at: null,
      is_active: false
    })
    setIsCreate(true)
    setIsDuplicate(true)
    setOpenModal(true)
  }, [])

  const handleSubmit = useCallback(
    async (data: CreateChallengeInput | UpdateChallengeInput, id?: string) => {
      setSubmitting(true)
      try {
        if (isCreate || isDuplicate) {
          await challengeService.create(data as CreateChallengeInput)
        } else if (id) {
          await challengeService.update(id, data as UpdateChallengeInput)
        }
        closeModal()
        refreshTable()
      } finally {
        setSubmitting(false)
      }
    },
    [isCreate, isDuplicate, closeModal, refreshTable]
  )

  const handleDelete = useCallback(
    async (row: GameChallenge) => {
      await challengeService.remove(row.id)
      refreshTable()
    },
    [t, refreshTable]
  )

  const handleToggleActive = useCallback(
    async (row: GameChallenge) => {
      if (row.is_active) {
        await challengeService.deactivate(row.id)
      } else {
        await challengeService.activate(row.id)
      }
      refreshTable()
    },
    [refreshTable]
  )

  const handleStatusFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value as ChallengeStatusFilter)
  }, [])

  const actions: TableAction[] = useMemo(
    () => [
      {
        name: t("challenges.actions.edit"),
        handler: openEdit,
        condition: (row: GameChallenge) => !isFinished(row)
      },
      {
        name: t("challenges.actions.duplicate"),
        handler: openDuplicate
      },
      {
        name: t("challenges.actions.toggle"),
        handler: handleToggleActive,
        condition: (row: GameChallenge) => !isFinished(row)
      },
      {
        name: t("challenges.actions.delete"),
        handler: handleDelete,
        buttonColor: "danger",
        condition: (row: GameChallenge) => !isFinished(row)
      }
    ],
    [t, openEdit, openDuplicate, handleToggleActive, handleDelete]
  )

  const modalTitle = useMemo(() => {
    if (isDuplicate) return t("challenges.duplicateChallenge")
    if (isCreate) return t("challenges.createChallenge")
    return t("challenges.editChallenge")
  }, [isDuplicate, isCreate, t])

  return (
    <div className="grid w-full gap-8 p-8 pt-0 grid-col-1">
      <TopBar title={t("challenges.title")} />

      <div className="p-4 bg-base-200 rounded-lg">
        <h3 className="font-bold mb-2">{t("challenges.overview.title")}</h3>
        <p className="text-sm text-base-content/70">{t("challenges.overview.description")}</p>

        <div className="mt-4">
          <h4 className="font-semibold">{t("challenges.overview.statusFiltersTitle")}</h4>
          <ul className="list-disc list-inside text-sm text-base-content/70">
            {STATUS_FILTER_DESCRIPTION_KEYS.map(({ key, labelKey, descriptionKey }) => (
              <li key={key}>
                <strong>{t(labelKey)}:</strong> {t(descriptionKey)}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-4">
          <h4 className="font-semibold">{t("challenges.overview.columnsTitle")}</h4>
          <ul className="list-disc list-inside text-sm text-base-content/70">
            {COLUMN_DESCRIPTION_KEYS.map(({ key, labelKey, descriptionKey }) => (
              <li key={key}>
                <strong>{t(labelKey)}:</strong> {t(descriptionKey)}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-4 items-center">
          <DropdownMenu
            placeholder="Status"
            options={localizedStatusOptions}
            value={statusFilter}
            onChange={handleStatusFilterChange}
            rounded
            hideEmptyOption
            disableEmptyOption
          />
        </div>
        <button className="btn btn-primary" onClick={openCreate}>
          {t("challenges.createChallenge")}
        </button>
      </div>

      <Table
        key={tableKey}
        title={t("challenges.title")}
        handler={tableHandler}
        columns={localizedColumns}
        actions={actions}
        isExport={false}
        isColumnsFilterable={false}
      />

      <Modal open={openModal} handler={closeModal}>
        <ChallengeForm
          initial={editRow ?? undefined}
          onSubmit={handleSubmit}
          onCancel={closeModal}
          loading={submitting}
          title={modalTitle}
        />
      </Modal>
    </div>
  )
}
